import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, User } from 'lucide-react';
import { AnchoredDropdownMenu } from './AnchoredDropdownMenu';
import { cn } from '@/lib/utils';
import { getUserData } from '@/utils/cookies';
import { getImageUrl } from '@/utils/imageUrl';
import { useSidebar } from '@/context/SidebarContext';
import { useGetUser } from '@/services';
import { E2E_HEADER_USER } from '@/constants/e2eSchedulingTestIds';

type ProfileAvatarProps = {
	photoUrl: string | null;
	size: 'trigger' | 'menu';
};

const ProfileAvatar = ({ photoUrl, size }: ProfileAvatarProps) => {
	const [failed, setFailed] = useState(false);
	const showPhoto = Boolean(photoUrl) && !failed;

	if (showPhoto && photoUrl) {
		return (
			<img
				src={photoUrl}
				alt=""
				referrerPolicy="no-referrer"
				onError={() => setFailed(true)}
				className={cn(
					'rounded-full object-cover',
					size === 'trigger' ? 'size-full' : 'size-14',
				)}
			/>
		);
	}

	return (
		<User
			className={cn(
				'shrink-0',
				size === 'trigger'
					? 'size-5 text-white/90'
					: 'size-8 text-stone-500',
			)}
			strokeWidth={2}
		/>
	);
};

const UserHeaderProfileMenu = () => {
	const navigate = useNavigate();
	const [open, setOpen] = useState(false);
	const { handleLogout: logout } = useSidebar();
	const { data: userFromApi } = useGetUser(
		{ isMe: true },
		{ refetchOnWindowFocus: false },
	);
	const user = userFromApi ?? getUserData();

	if (!user?.id) {
		return null;
	}

	const rawPhotoUrl = user.profile_photo_url?.trim() || null;
	const photoUrl = rawPhotoUrl ? getImageUrl(rawPhotoUrl) : null;
	const fullName =
		[user.first_name, user.last_name].filter(Boolean).join(' ').trim() ||
		'Usuário';
	const email = user.email ?? '';

	const closeMenu = () => setOpen(false);

	const handleProfile = () => {
		closeMenu();
		navigate('/perfil');
	};

	const onLogout = () => {
		closeMenu();
		logout();
	};

	return (
		<AnchoredDropdownMenu
			open={open}
			onOpenChange={setOpen}
			menuWidth={288}
			estimatedHeight={220}
			menuClassName="w-72 max-w-[calc(100vw-1rem)] overflow-x-hidden p-0 animate-in fade-in zoom-in-95 duration-100"
			trigger={({
				ref: setAnchorRef,
				isOpen: menuOpen,
				onTriggerClick,
			}) => (
				<button
					ref={setAnchorRef}
					type="button"
					onClick={onTriggerClick}
					className={cn(
						'inline-flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-white/25 bg-white/10 transition-colors hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40',
						photoUrl && 'p-0',
					)}
					aria-label="Abrir menu do perfil"
					aria-expanded={menuOpen}
					aria-haspopup="menu"
				>
					<ProfileAvatar photoUrl={photoUrl} size="trigger" />
				</button>
			)}
		>
			<div className="flex flex-col">
				<div className="flex items-center gap-3 border-b border-stone-100 px-4 py-4">
					<div className="flex size-14 shrink-0 items-center justify-center overflow-hidden rounded-full">
						<ProfileAvatar photoUrl={photoUrl} size="menu" />
					</div>

					<div className="min-w-0 flex-1 text-left">
						<p
							className="truncate text-base font-bold text-stone-900"
							title={fullName}
						>
							{fullName}
						</p>

						{email ? (
							<p
								className="mt-0.5 truncate text-xs text-stone-400"
								title={email}
							>
								{email}
							</p>
						) : null}
					</div>
				</div>

				<button
					type="button"
					onClick={handleProfile}
					className="flex w-full items-center gap-3 px-4 py-3.5 text-left text-sm font-semibold text-stone-700 transition-colors hover:bg-stone-50 focus:outline-none"
					role="menuitem"
				>
					<User
						size={18}
						className="shrink-0 text-stone-500"
						strokeWidth={2}
					/>
					Meu perfil
				</button>

				<div className="border-t border-stone-100">
					<button
						type="button"
						onClick={onLogout}
						data-testid={E2E_HEADER_USER.LOGOUT}
						className="flex w-full items-center gap-3 px-4 py-3.5 text-left text-sm font-bold text-red-600 transition-colors hover:bg-red-50 focus:outline-none"
						role="menuitem"
					>
						<LogOut
							size={18}
							className="shrink-0"
							strokeWidth={2.25}
						/>
						Sair
					</button>
				</div>
			</div>
		</AnchoredDropdownMenu>
	);
};

export { UserHeaderProfileMenu };
